import { AssetVisitType, LoadTypeDirection, LoadTypeOperation, type Nullable } from '@nova/core';

const loadTypeAssetVisitMap: Partial<
  Record<LoadTypeDirection, Partial<Record<LoadTypeOperation, AssetVisitType>>>
> = {
  [LoadTypeDirection.Inbound]: {
    [LoadTypeOperation.Live]: AssetVisitType.LiveUnload,
    [LoadTypeOperation.Drop]: AssetVisitType.Drop
  },
  [LoadTypeDirection.Outbound]: {
    [LoadTypeOperation.Live]: AssetVisitType.LiveLoad,
    [LoadTypeOperation.Drop]: AssetVisitType.PickUp
  },
  [LoadTypeDirection.InOut]: {
    [LoadTypeOperation.Live]: AssetVisitType.Live,
    [LoadTypeOperation.Drop]: AssetVisitType.DropHook
  }
};

// Load types may carry no operation (or `Other`), and legacy directions predate Inbound/Outbound.
// Falling back on direction alone keeps a planned check-in from submitting a null visit type.
const directionAssetVisitMap: Record<LoadTypeDirection, AssetVisitType> = {
  [LoadTypeDirection.Inbound]: AssetVisitType.LiveUnload,
  [LoadTypeDirection.Unload]: AssetVisitType.LiveUnload,
  [LoadTypeDirection.Outbound]: AssetVisitType.LiveLoad,
  [LoadTypeDirection.Load]: AssetVisitType.LiveLoad,
  [LoadTypeDirection.InOut]: AssetVisitType.Live
};

/**
 * Mirrors the direction+operation combinations a driver could otherwise pick manually on
 * PurposeOfStopPage, so a planned check-in's load type resolves to the same AssetVisitType an
 * equivalent unplanned check-in would send.
 */
export function inferAssetVisitTypeFromLoadType(
  loadType:
    | { direction?: Nullable<LoadTypeDirection>; operation?: Nullable<LoadTypeOperation> }
    | null
    | undefined
): Nullable<AssetVisitType> {
  const { direction, operation } = loadType ?? {};

  if (!direction) {
    return null;
  }

  return (
    (operation && loadTypeAssetVisitMap[direction]?.[operation]) ||
    directionAssetVisitMap[direction] ||
    null
  );
}

const directionI18nKeyMap: Record<LoadTypeDirection, string> = {
  [LoadTypeDirection.Inbound]: 'inbound',
  [LoadTypeDirection.Outbound]: 'outbound',
  [LoadTypeDirection.InOut]: 'inOut',
  [LoadTypeDirection.Load]: 'load',
  [LoadTypeDirection.Unload]: 'unload'
};

/**
 * The API returns directions as English enum values, and the cards showing them sit inside
 * `comet-i18n-exclude`, so GlobalSEO never reaches them. Mapping to a key keeps them translated.
 */
export function getLoadTypeDirectionI18nKey(
  direction: Nullable<LoadTypeDirection> | undefined
): string | null {
  return direction ? (directionI18nKeyMap[direction] ?? null) : null;
}
